import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { FiSearch, FiTrendingUp } from "react-icons/fi";
import { contentAPI } from "../api/endpoints";
import ContentCard from "../components/ContentCard";

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const query = searchParams.get("q") || "";

  const [results, setResults] = useState([]);
  const [topSearch, setTopSearch] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [searchInput, setSearchInput] = useState(query);

  // Load top/trending searches on mount
  useEffect(() => {
    contentAPI
      .getTopSearch()
      .then((res) => {
        const data = res.data;
        // Flat array: [{ id, type, name, image, ... }]
        // or grouped: [{ title, data: [movies] }]
        let items = [];
        if (Array.isArray(data) && data.length > 0 && data[0]?.data) {
          data.forEach((group) => {
            if (Array.isArray(group.data)) items.push(...group.data);
          });
        } else if (Array.isArray(data)) {
          items = data;
        } else if (data && typeof data === "object") {
          items = data.data || data.movies || data.results || [];
        }
        setTopSearch(items);
      })
      .catch(() => {});
  }, []);

  // Search when query param changes
  const doSearch = useCallback((q) => {
    if (!q.trim()) {
      setResults([]);
      setSearched(false);
      return;
    }
    setLoading(true);
    setSearched(true);
    contentAPI
      .search(q)
      .then((res) => {
        const data = res.data;
        // Response is grouped by genre: [{ title, type, data: [movies] }]
        // Flatten all items from all groups into a single array
        let items = [];
        if (Array.isArray(data) && data.length > 0 && data[0]?.data) {
          data.forEach((group) => {
            if (Array.isArray(group.data)) {
              items.push(...group.data);
            }
          });
        } else if (Array.isArray(data)) {
          items = data;
        } else {
          items = data?.results || data?.data || [];
        }
        // Deduplicate by id
        const seen = new Set();
        items = items.filter((item) => {
          if (seen.has(item.id)) return false;
          seen.add(item.id);
          return true;
        });
        setResults(items);
      })
      .catch(() => setResults([]))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    doSearch(query);
  }, [query, doSearch]);

  // Sync input with URL param
  useEffect(() => {
    setSearchInput(query);
  }, [query]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (searchInput.trim()) {
      setSearchParams({ q: searchInput.trim() });
    }
  };

  const handleTopSearchClick = (item) => {
    const title = item.title || item.name;
    if (title) {
      setSearchInput(title);
      setSearchParams({ q: title });
    }
  };

  return (
    <div className="min-h-screen bg-dark-900 pt-20 pb-16">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        {/* Search Input */}
        <form onSubmit={handleSubmit} className="relative mb-8">
          <FiSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={20} />
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search movies, TV shows..."
            className="w-full pl-12 pr-4 py-4 bg-dark-700 border border-dark-400 rounded-2xl text-white text-base placeholder:text-gray-500 focus:border-primary focus:ring-1 focus:ring-primary/30 outline-none transition-all"
            autoFocus
          />
        </form>

        {/* Search Results */}
        {query && (
          <>
            <p className="text-gray-500 text-sm mb-6">
              Showing results for "<span className="text-white">{query}</span>"
            </p>

            {loading ? (
              <div className="flex justify-center py-20">
                <div className="w-10 h-10 border-3 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : results.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
                {results.map((item) => (
                  <ContentCard key={item.id} item={item} type={item.type} fillWidth />
                ))}
              </div>
            ) : searched ? (
              <div className="flex flex-col items-center justify-center py-20 text-gray-500">
                <FiSearch size={48} className="mb-4 text-gray-700" />
                <p className="text-lg font-medium">No results found</p>
                <p className="text-sm mt-1">Try searching with different keywords</p>
              </div>
            ) : null}
          </>
        )}

        {/* Top / Trending Searches — shown when no active search */}
        {!query && topSearch.length > 0 && (
          <div>
            <h2 className="text-xl font-bold text-white mb-5 flex items-center gap-2">
              <FiTrendingUp size={20} className="text-primary" />
              Trending Searches
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
              {topSearch.map((item) => (
                <ContentCard key={item.id} item={item} type={item.type} fillWidth />
              ))}
            </div>
          </div>
        )}

        {/* Empty state — no query and no top searches */}
        {!query && topSearch.length === 0 && !loading && (
          <div className="flex flex-col items-center justify-center py-20 text-gray-500">
            <FiSearch size={48} className="mb-4 text-gray-700" />
            <p className="text-lg font-medium">Search for movies & TV shows</p>
            <p className="text-sm mt-1">Find your favorite content</p>
          </div>
        )}
      </div>
    </div>
  );
}
